// texe-web/src/lib/texeApi.ts

export type ApiConfig = {
  /** Varsayılan: window.location.origin ya da http://localhost:5000 */
  baseUrl?: string;
  /** Cookie tabanlı auth için true bırak */
  withCredentials?: boolean;
  /** Bearer JWT kullanacaksan ayarla */
  authToken?: string;
  /** İstek zaman aşımı (ms) — varsayılan 20sn */
  timeoutMs?: number;
};

export type ProblemJson = {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  meta?: Record<string, unknown>;
  // sunucudan gelebilecek ek alanlar
  [k: string]: unknown;
};

/** Texe API – hafif fetch wrapper */
export class TexeApi {
  private _baseUrl: string;
  private withCredentials: boolean;
  private authToken?: string;
  private timeoutMs: number;

  constructor(cfg: ApiConfig = {}) {
    // Production'da doğrudan backend'e bağlan, development'ta localhost
    const isProduction = typeof window !== "undefined" && window.location.hostname !== "localhost";
    const productionBase = "https://api.savnot.com";
    const developmentBase = "http://localhost:5003"; // Backend port: 5003

    // Vite ortam değişkeni ile override imkânı
    const envBase =
      (typeof import.meta !== "undefined" && (
        (import.meta as any).env?.VITE_API_URL ||
        (import.meta as any).env?.VITE_TEXE_API_BASE ||
        (import.meta as any).env?.VITE_API_BASE
      )) || undefined;

    this._baseUrl = cfg.baseUrl || envBase || (isProduction ? productionBase : developmentBase);
    this.withCredentials = cfg.withCredentials ?? true;
    this.authToken = cfg.authToken;
    this.timeoutMs = cfg.timeoutMs ?? 20_000;
  }

  get baseUrl(): string {
    return this._baseUrl;
  }

  setAuthToken(token?: string) {
    this.authToken = token;
  }

  private headers(extra?: HeadersInit): HeadersInit {
    const h: Record<string, string> = {
      Accept: "application/json, application/problem+json",
      ...(extra as Record<string, string>),
    };
    if (this.authToken) h.Authorization = `Bearer ${this.authToken}`;
    return h;
  }

  /** Problem+JSON farkındalığı olan istek */
  private async req<T>(path: string, init: RequestInit = {}): Promise<T> {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const doFetch = (override?: RequestInit) =>
        fetch(this._baseUrl + path, {
          ...init,
          ...override,
          headers: this.headers((override?.headers as any) ?? (init.headers as any)),
          credentials: this.withCredentials ? "include" : "same-origin",
          signal: controller.signal,
        });

      let res = await doFetch();

      // 401 için otomatik refresh denemesi (bir kez)
      if (res.status === 401 && !path.startsWith("/api/auth/")) {
        const refreshed = await this.tryRefreshAccess();
        if (refreshed) {
          res = await doFetch();
        }
      }

      const ct = (res.headers.get("content-type") || "").toLowerCase();

      // Problem Details (RFC7807)
      if (ct.includes("application/problem+json")) {
        const pb: ProblemJson = await safeJson(res);
        const msg = pb.detail || pb.title || res.statusText;
        const err = new Error(String(msg));
        (err as any).problem = pb;
        (err as any).status = res.status;
        throw err;
      }

      // Normal JSON
      if (ct.includes("application/json")) {
        const body = await res.json();
        if (!res.ok) {
          const err = new Error(String(body?.error || body?.message || res.statusText));
          (err as any).status = res.status;
          (err as any).body = body;
          throw err;
        }
        return body as T;
      }

      // Diğer içerikler (metin, blob vs.)
      const text = await res.text();
      if (!res.ok) {
        const err = new Error(text || res.statusText);
        (err as any).status = res.status;
        (err as any).body = text;
        throw err;
      }
      return text as T;
    } finally {
      clearTimeout(id);
    }
  }

  /** 401 sonrasında cookie tabanlı refresh denemesi */
  private async tryRefreshAccess(): Promise<boolean> {
    try {
      const res = await fetch(this._baseUrl + "/api/auth/refresh", {
        method: "POST",
        headers: this.headers({ "Content-Type": "application/json" }),
        credentials: this.withCredentials ? "include" : "same-origin",
        body: JSON.stringify({}),
      });
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (!ct.includes("application/json")) return false;
      if (!res.ok) return false;
      const data = await res.json();
      const t = data?.access || data?.accessToken || data?.token;
      if (t) {
        this.setAuthToken(t);
        try { localStorage.setItem("accessToken", t); } catch {}
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  // ───────────────── Helpers (şeker fonksiyonlar) ─────────────────
  get<T>(path: string) {
    return this.req<T>(path);
  }

  post<T>(path: string, body?: unknown) {
    return this.req<T>(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body ?? {}),
    });
  }

  patch<T>(path: string, body?: unknown) {
    return this.req<T>(path, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body ?? {}),
    });
  }

  put<T>(path: string, body?: unknown) {
    return this.req<T>(path, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body ?? {}),
    });
  }

  upload<T>(path: string, formData: FormData, init?: RequestInit) {
    return this.req<T>(path, {
      method: init?.method ?? "POST",
      body: formData,
      headers: init?.headers,
    });
  }

  postJson<T>(path: string, body: unknown) {
    return this.post<T>(path, body);
  }

  patchJson<T>(path: string, body: unknown) {
    return this.patch<T>(path, body);
  }

  delete<T>(path: string) {
    return this.req<T>(path, { method: "DELETE" });
  }

  // ───────────────── Profile ─────────────────
  getProfile() {
    return this.get<{ ok: boolean; user: any }>("/api/profile/me");
  }

  patchProfile(data: {
    bio?: string | null;
    favoriteQuote?: string | null;
    website?: string | null;
    preferredLanguage?: string | null;
  }) {
    return this.patchJson<{ ok: boolean; user: any }>("/api/profile/me", data);
  }

  async uploadAvatar(file: File) {
    const fd = new FormData();
    fd.append("avatar", file);
    return this.req<{ ok: boolean; user: any; avatarUrl?: string; avatarUrlSmall?: string }>(
      "/api/profile/avatar",
      { method: "POST", body: fd }
    );
  }

  deleteAvatar() {
    return this.delete<{ ok: boolean; user: any }>("/api/profile/avatar");
  }

  // ───────────────── Admin / Audit ─────────────────
  listAudit(params?: {
    action?: string;
    target?: string;
    actor?: string;
    before?: string;
    limit?: number;
  }) {
    const sp = new URLSearchParams();
    if (params?.action) sp.set("action", params.action);
    if (params?.target) sp.set("target", params.target);
    if (params?.actor) sp.set("actor", params.actor);
    if (params?.before) sp.set("before", params.before);
    if (params?.limit) sp.set("limit", String(params.limit));
    const qs = sp.toString();
    return this.get<{ ok: boolean; count: number; rows: any[] }>(
      `/api/admin/audit${qs ? "?" + qs : ""}`
    );
  }

  // ───────────────── Admin / Usernames ─────────────────
  listUsernameReservations(q?: string) {
    const qs = q ? `?q=${encodeURIComponent(q)}` : "";
    return this.get<{
      ok: true;
      items: Array<{
        name: string;
        until: string;
        reason: string;
        byUserId: string | null;
        createdAt?: string;
        updatedAt?: string;
      }>;
    }>(`/api/admin/usernames/reservations${qs}`);
  }

  reserveUsername(input: {
    name: string;
    until?: string; // ISO
    reason?: "rename" | "soft_delete" | "manual";
    byUserId?: string;
  }) {
    return this.postJson(`/api/admin/usernames/reservations`, input) as Promise<{
      ok: true;
      item: { name: string; until: string; reason: string; byUserId: string | null };
    }>;
  }

  deleteUsernameReservation(name: string) {
    return this.delete<{ ok: true; deleted: true }>(
      `/api/admin/usernames/reservations/${encodeURIComponent(name)}`
    );
  }

  transferUsername(input: {
    desired: string;
    toUserId: string;
    fromUserId?: string;
  }) {
    return this.postJson(`/api/admin/usernames/transfer`, input) as Promise<{
      ok: boolean;
      transferred: boolean;
      username?: string;
      message?: string;
    }>;
  }

  // ───────────────── Moderation ─────────────────
  modDeletePost(id: string) {
    return this.delete<{ ok: boolean }>(`/api/mod/posts/${encodeURIComponent(id)}`);
  }
  modDeleteComment(id: string) {
    return this.delete<{ ok: boolean }>(`/api/mod/comments/${encodeURIComponent(id)}`);
  }

  // ───────────────── Realtime (SSE) ─────────────────
  /**
   * Basit SSE bağlayıcı. onEvent ile özel event’leri de yakalayabilirsin.
   * Dönüş: EventSource ve kapatma yardımcıları.
   */
  sseConnect(opts: {
    onMessage?: (ev: MessageEvent) => void;
    onEvent?: (type: string, data: any, raw: MessageEvent) => void;
    onError?: (ev: Event) => void;
  } = {}) {
    const url = this._baseUrl + "/api/realtime/stream";
    const es = new EventSource(url, { withCredentials: this.withCredentials });

    es.onmessage = (ev) => {
      // Backend "event: message\ndata: {...}" kullanıyor
      if (opts.onMessage) opts.onMessage(ev);
      if (opts.onEvent) {
        try {
          const data = JSON.parse(ev.data);
          const type = (data && data.type) || "message";
          opts.onEvent(type, data, ev);
        } catch {
          opts.onEvent?.("message", ev.data, ev);
        }
      }
    };
    es.onerror = (ev) => opts.onError?.(ev);

    return {
      source: es,
      close: () => es.close(),
    };
  }
}

// ───────────────── Dahili yardımcılar ─────────────────
async function safeJson(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return {};
  }
}
